<template>
  <div class="fixed inset-0 pointer-events-none z-[3000]">
    <div class="absolute top-3 right-3 w-[min(92vw,380px)] space-y-2">
      <TransitionGroup name="notif" tag="div">
        <div
          v-for="n in notifications.items"
          :key="n.id"
          class="pointer-events-auto flex items-start mt-2 gap-3 p-3 rounded-lg border shadow-xl backdrop-blur"
          :class="containerClass(n.status)"
        >
          <div class="w-1.5 h-7 mt-0.5 rounded-full" :class="barClass(n.status)" />
          <div :class="iconWrapClass(n.status)">
            <component :is="iconOf(n.status)" class="h-4 w-4" :class="iconClass(n.status)" />
          </div>
          <div class="min-w-0">
            <div v-if="n.title" class="text-sm font-medium text-white truncate">{{ n.title }}</div>
            <div class="text-xs break-words whitespace-pre-wrap" :class="textClass(n.status)">{{ n.message }}</div>
          </div>
          <button type="button" class="ml-auto text-secondary-400 hover:text-white/90" @click="dismiss(n.id)">×</button>
        </div>
      </TransitionGroup>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useNotifications, remove } from '@/lib/notifications'
import { CheckCircle, Info, AlertTriangle, XCircle } from 'lucide-vue-next'

const notifications = useNotifications()

function iconOf(status: 'success' | 'info' | 'warning' | 'error') {
  if (status === 'success') return CheckCircle
  if (status === 'warning') return AlertTriangle
  if (status === 'error') return XCircle
  return Info
}
function iconClass(status: 'success' | 'info' | 'warning' | 'error') {
  if (status === 'success') return 'text-emerald-100'
  if (status === 'warning') return 'text-amber-100'
  if (status === 'error') return 'text-red-100'
  return 'text-blue-100'
}
function iconWrapClass(status: 'success' | 'info' | 'warning' | 'error') {
  if (status === 'success') return 'mt-0.5 inline-flex items-center justify-center h-7 w-7 rounded-full bg-emerald-500/20 ring-1 ring-emerald-400/30'
  if (status === 'warning') return 'mt-0.5 inline-flex items-center justify-center h-7 w-7 rounded-full bg-amber-500/20 ring-1 ring-amber-400/30'
  if (status === 'error') return 'mt-0.5 inline-flex items-center justify-center h-7 w-7 rounded-full bg-red-500/20 ring-1 ring-red-400/30'
  return 'mt-0.5 inline-flex items-center justify-center h-7 w-7 rounded-full bg-blue-500/20 ring-1 ring-blue-400/30'
}
function barClass(status: 'success' | 'info' | 'warning' | 'error') {
  if (status === 'success') return 'bg-emerald-500/70'
  if (status === 'warning') return 'bg-amber-500/70'
  if (status === 'error') return 'bg-red-500/70'
  return 'bg-blue-500/70'
}
function containerClass(status: 'success' | 'info' | 'warning' | 'error') {
  if (status === 'success') return 'bg-emerald-950/70 border-emerald-800/60 shadow-emerald-900/40'
  if (status === 'warning') return 'bg-amber-950/70 border-amber-800/60 shadow-amber-900/40'
  if (status === 'error') return 'bg-red-950/70 border-red-800/60 shadow-red-900/40'
  return 'bg-blue-950/70 border-blue-800/60 shadow-blue-900/40'
}
function textClass(status: 'success' | 'info' | 'warning' | 'error') {
  if (status === 'success') return 'text-emerald-100'
  if (status === 'warning') return 'text-amber-100'
  if (status === 'error') return 'text-red-100'
  return 'text-blue-100'
}
function dismiss(id: number) {
  try { remove(id) } catch {}
}
</script>

<style scoped>
.notif-enter-from,
.notif-leave-to { opacity: 0; transform: translateY(-6px) scale(0.98); }
.notif-enter-active,
.notif-leave-active { transition: opacity .15s ease, transform .15s ease; }
</style>


